import { useTranslation } from 'react-i18next';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { isIOSSafari } from '@/utils/platform';

export const InstallAppCard = () => {
  const { t } = useTranslation('common');
  const { isInstallable, isInstalled, promptInstall } = useInstallPrompt();

  if (isInstalled) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm h-full flex flex-col">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
        <div className="p-6 sm:p-8 flex-1 flex flex-col items-center justify-center text-center gap-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white shadow-sm flex items-center justify-center ring-1 ring-gray-100">
            <img src="/pwa-192x192.png" alt="" aria-hidden="true" className="w-12 h-12 sm:w-14 sm:h-14 object-contain" />
          </div>
          <div className="space-y-1.5">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center justify-center gap-2">
              <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-green-600" ariaHidden />
              {t('install_app_installed_title', { defaultValue: 'App installed' })}
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed max-w-sm">
              {t('install_app_installed_description', {
                defaultValue: 'Launch Ägile Ässets from your home screen or desktop.',
              })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const ios = isIOSSafari();

  return (
    <div className="relative overflow-hidden rounded-2xl border-2 border-primary-200 bg-gradient-to-br from-primary-50 via-white to-soft-mint shadow-md h-full flex flex-col">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
      <div className="p-6 sm:p-8 flex-1 flex flex-col items-center justify-center text-center gap-4">
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white shadow-sm flex items-center justify-center ring-1 ring-gray-100">
          <img
            src="/pwa-192x192.png"
            alt=""
            aria-hidden="true"
            className="w-12 h-12 sm:w-14 sm:h-14 object-contain"
          />
        </div>
        <div className="space-y-1.5">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            {t('install_banner_title')}
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed max-w-sm">
            {ios
              ? t('install_app_ios_instructions', {
                  defaultValue: 'In Safari, tap the Share icon, then choose "Add to Home Screen".',
                })
              : t('install_banner_description')}
          </p>
        </div>
        <div>
          {isInstallable ? (
            <Button
              variant="primary"
              onClick={() => { void promptInstall(); }}
              className="gap-1.5"
            >
              <Icon name={ICONS.DOWNLOAD} size="sm" ariaHidden />
              {t('install_banner_install')}
            </Button>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
              <Icon name={ICONS.INFO} size="xs" ariaHidden />
              {ios
                ? t('install_app_ios_badge', { defaultValue: 'Manual install' })
                : t('install_app_unsupported_badge', { defaultValue: 'Use your browser menu' })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
